import React, { useMemo } from 'react';
import {
  alphanumericRegExp,
  Box,
  Button,
  InputLabel,
  KeyValue,
  numericRegExp,
  TextFieldForm,
  Typography,
} from 'web-library';
import { TFunction, useTranslation } from 'react-i18next';
import { FormControlLabel, Radio, useMediaQuery, useTheme } from '@mui/material';

import { UseFormReturn } from 'react-hook-form';

import ToggleDisplayEditFormItem from '../../../../../components/ToggleDisplayEditFormItem';
import { useStyles } from '../styles';
import HideOrShow from './HideOrShow';
import {
  fallbackEmptyString,
  prefix0For2Digits,
  stringIsEmpty,
} from '../../../../../utils/StringUtils';
import { getAddressDisplayArray } from '../../../../../utils/ComponentUtils';
import {
  CandidateProfileFormData,
  CandidateProfileFormDefaultValues,
  CandidateProfileIsDisableInputs,
} from '../../../../../models';
import LocalAddressHelper from '../../../../../components/helper-text/LocalAddressHelper';
import { useGetAddressDetailsByPostcode } from '../../../../accountManagement/logic/useGetAddressDetailsByPostcode';

const getMailingAddressDisplayText = (
  houseOrBlkNo = '',
  streetName = '',
  building = '',
  floor = '',
  unit = '',
  postalCode = ''
): string[] => {
  const displayTexts: string[] = [
    fallbackEmptyString(houseOrBlkNo),
    fallbackEmptyString(streetName),
    fallbackEmptyString(building),
  ];

  let floorAndUnit: string = '';
  if (!stringIsEmpty(floor) && !stringIsEmpty(unit)) {
    floorAndUnit = `#${floor}-${unit}`;
  } else if (!stringIsEmpty(floor)) {
    floorAndUnit = `#${floor}`;
  } else if (!stringIsEmpty(unit)) {
    floorAndUnit = `-${unit}`;
  }
  displayTexts.push(floorAndUnit);

  if (!stringIsEmpty(postalCode)) {
    displayTexts.push(`Singapore ${postalCode}`);
  }

  return getAddressDisplayArray(displayTexts);
};

export interface AddressProps {
  isEditState: boolean;
  formHook?: UseFormReturn<any>;
  isDisableInputs?: CandidateProfileIsDisableInputs;
  isHideExtraFormInfo?: boolean;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  formDefaultValuesViewDetails?: CandidateProfileFormDefaultValues;
  isRequired?: boolean;
  label?: string;
  disableLabel?: boolean;
  formControlLabel?: string;
  sameRowAddress?: boolean;
  isNonNational?: boolean;
}

const houseOrBlkNoDefaultValue = (
  isNonNational: boolean,
  formDefaultValues: CandidateProfileFormDefaultValues | undefined
) => (isNonNational ? formDefaultValues?.houseOrBlkNo : '');

const houseOrBlkNoReadOnlyStatus = (isDisableInputs: CandidateProfileIsDisableInputs | undefined) =>
  isDisableInputs?.houseOrBlkNo;

const preferredAddressKeyText = (
  formDefaultValues: CandidateProfileFormDefaultValues | undefined
) => (formDefaultValues?.preferredAddress === 'L' ? 'Preferred Address' : undefined);

const floorReadOnlyStatus = (isDisableInputs: CandidateProfileIsDisableInputs | undefined) =>
  isDisableInputs?.floor;

const unitReadOnlyStatus = (isDisableInputs: CandidateProfileIsDisableInputs | undefined) =>
  isDisableInputs?.unit;

const postalCodeDefaultValue = (
  isNonNational: boolean,
  formDefaultValues: CandidateProfileFormDefaultValues | undefined
) => (isNonNational ? formDefaultValues?.postalCode : '');

const postalCodeReadOnlyStatus = (isDisableInputs: CandidateProfileIsDisableInputs | undefined) =>
  isDisableInputs?.postalCode;

const getRequiredLabel = (
  classes: Record<string,string>,
  disableLabel: boolean,
  isRequired: boolean,
  label: string | undefined,
  defaultLabel: string
) => (
  <Box className={disableLabel ? classes.disabled : ''}>
    {label ?? defaultLabel}
    {isRequired && !disableLabel && <div className={classes.requiredLabel}>*</div>}
    {isRequired && disableLabel && <div className={classes.disabledLabel}>*</div>}
  </Box>
);

const ReadStateAddress = ({
  defaultLabel,
  sameRowAddress,
  label,
  formDefaultValuesViewDetails,
}: {
  defaultLabel: string;
  sameRowAddress: boolean;
  formDefaultValuesViewDetails?: CandidateProfileFormDefaultValues;
  label?: string;
}) => {
  const { classes } = useStyles();
  return (
    <Box className={`${sameRowAddress && classes.halfWidthLeftItem}`}>
      <KeyValue
        keyText={label ?? defaultLabel}
        keyIsPrefferedText={preferredAddressKeyText(formDefaultValuesViewDetails)}
        valueText={getMailingAddressDisplayText(
          formDefaultValuesViewDetails?.houseOrBlkNo,
          formDefaultValuesViewDetails?.streetName,
          formDefaultValuesViewDetails?.building,
          formDefaultValuesViewDetails?.floor,
          formDefaultValuesViewDetails?.unit,
          formDefaultValuesViewDetails?.postalCode
        )}
      />
    </Box>
  );
};

interface ResidentialAddressBlkNoProps {
  isEditState: boolean;
  classes: Record<string,string>;
  t: TFunction<'translation'>;
  isNonNational: boolean;
  isDisableInputs?: CandidateProfileIsDisableInputs;
  formHook?: UseFormReturn<CandidateProfileFormData>;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  readonly: boolean;
}

const ResidentAddressBlkNo = ({
  isEditState,
  classes,
  t,
  isNonNational,
  isDisableInputs,
  formHook,
  formDefaultValues,
  readonly,
}: ResidentialAddressBlkNoProps) => (
  <ToggleDisplayEditFormItem
    isEditState={isEditState}
    isHideDisplay
    id="inputText-residentialAddress-blkNo"
    containerClassName={classes.groupFormElementContainer}
    EditComponent={
      <TextFieldForm
        id="inputText-residentialAddress-blkNo"
        form={formHook}
        isInputTextUpperCase
        onChange={(e) => formHook?.setValue('houseOrBlkNo', e.target.value.toUpperCase())}
        placeholder={t('profile->>CreateCandidateForm.House/Block No.')}
        defaultValue={houseOrBlkNoDefaultValue(isNonNational, formDefaultValues)}
        name="houseOrBlkNo"
        type="text"
        error={!!formHook?.formState.errors.houseOrBlkNo}
        helperText={formHook?.formState.errors?.houseOrBlkNo?.message?.toString()}
        fullWidth
        inputProps={{ readOnly: houseOrBlkNoReadOnlyStatus(isDisableInputs) || readonly }}
      />
    }
  />
);

const ResidentialAddress = ({
  isEditState,
  formHook,
  isDisableInputs = undefined,
  isHideExtraFormInfo = false,
  formDefaultValues = undefined,
  formDefaultValuesViewDetails = undefined,
  isRequired = false,
  label,
  disableLabel = false,
  formControlLabel,
  sameRowAddress = false,
  isNonNational = false,
}: AddressProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isShowExtraFormInfo = useMemo(() => !isHideExtraFormInfo && isEditState, [
    isHideExtraFormInfo,
    isEditState,
  ]);
  const defaultLabel = t('profile->>CreateCandidateForm.ResidentialAddress');
  const {
    isDisableAddr,
    validatePostalCode,
    isApiError,
    isPostalCodeEmpty,
    postcodeOnChange,
  } = useGetAddressDetailsByPostcode<CandidateProfileFormData>({
    blkNoInputName: 'houseOrBlkNo',
    streetInputName: 'streetName',
    buildingInputName: 'building',
    postcodeInputName: 'postalCode',
    isPostcodeEmptyDefaultValue: formDefaultValues?.postalCode?.length === 0,
    setValue: formHook?.setValue,
    getValues: formHook?.getValues,
    setError: formHook?.setError,
    clearErrors: formHook?.clearErrors,
    trigger: formHook?.trigger,
  });

  if (!isEditState) {
    return (
      <ReadStateAddress
        label={label}
        defaultLabel={defaultLabel}
        sameRowAddress={sameRowAddress}
        formDefaultValuesViewDetails={formDefaultValuesViewDetails}
      />
    );
  }
  return (
    <Box className={`${classes.responsiveFlexContainer} ${classes.formElementContainer}`}>
      <HideOrShow
        isHide={!isShowExtraFormInfo}
        Component={
          <Typography variant="subtitle1" className={classes.extraInfoText}>
            {t('profile->>CreateCandidateForm.AddressHelperText')}
          </Typography>
        }
      />
      <InputLabel>
        {getRequiredLabel(classes, disableLabel, isRequired, label, defaultLabel)}
      </InputLabel>
      <LocalAddressHelper className={classes.groupFormElementContainer} />
      <ResidentAddressBlkNo
        isEditState={isEditState}
        classes={classes}
        t={t}
        isNonNational={isNonNational}
        isDisableInputs={isDisableInputs}
        formHook={formHook}
        formDefaultValues={formDefaultValues}
        readonly={isDisableAddr}
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        isHideDisplay
        id="inputText-residentialAddress-streetName"
        containerClassName={classes.groupFormElementContainer}
        EditComponent={
          <TextFieldForm
            id="inputText-residentialAddress-streetName"
            form={formHook}
            isInputTextUpperCase
            onChange={(e) => formHook?.setValue('streetName', e.target.value.toUpperCase())}
            placeholder={t('profile->>CreateCandidateForm.Street Name')}
            defaultValue={isNonNational ? formDefaultValues?.streetName : ''}
            name="streetName"
            type="text"
            error={!!formHook?.formState.errors.streetName}
            helperText={formHook?.formState.errors?.streetName?.message?.toString()}
            fullWidth
            inputProps={{ readOnly: isDisableInputs?.streetName || isDisableAddr }}
          />
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        isHideDisplay
        id="inputText-residentialAddress-building"
        containerClassName={classes.groupFormElementContainer}
        EditComponent={
          <TextFieldForm
            id="inputText-residentialAddress-building"
            form={formHook}
            isInputTextUpperCase
            onChange={(e) => formHook?.setValue('building', e.target.value.toUpperCase())}
            placeholder={t('profile->>AddressHelper.BuildingPlaceholder')}
            defaultValue={isNonNational ? formDefaultValues?.building : ''}
            name="building"
            type="text"
            error={!!formHook?.formState.errors.building}
            helperText={formHook?.formState.errors?.building?.message?.toString()}
            fullWidth
            inputProps={{ readOnly: isDisableInputs?.building || isDisableAddr }}
          />
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        id="inputText-residentialAddress-floor"
        isHideDisplay
        containerClassName={`${classes.groupFormElementContainer} ${
          isMobile ? '' : classes.groupHalfWidthLeftItem
        }`}
        EditComponent={
          <TextFieldForm
            id="inputText-residentialAddress-floor"
            form={formHook}
            isInputTextUpperCase
            onChange={(e) => formHook?.setValue('floor', e.target.value.toUpperCase())}
            placeholder={t('profile->>AddressHelper.FloorPlaceholder')}
            defaultValue={isNonNational ? formDefaultValues?.floor : ''}
            name="floor"
            type="text"
            error={!!formHook?.formState.errors.floor}
            helperText={formHook?.formState.errors?.floor?.message?.toString()}
            fullWidth
            onBlur={(e) => formHook?.setValue('floor', prefix0For2Digits(e.target.value))}
            inputProps={{
              readOnly: floorReadOnlyStatus(isDisableInputs),
              startAdornment: '#',
            }}
            regExpToBlock={numericRegExp}
          />
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        isHideDisplay
        containerClassName={`${classes.groupFormElementContainer} ${
          isMobile ? '' : classes.groupHalfWidthRightItem
        }`}
        id="inputText-residentialAddress-unit"
        EditComponent={
          <TextFieldForm
            id="inputText-residentialAddress-unit"
            form={formHook}
            isInputTextUpperCase
            onChange={(e) => formHook?.setValue('unit', e.target.value.toUpperCase())}
            placeholder={t('profile->>AddressHelper.UnitPlaceholder')}
            defaultValue={isNonNational ? formDefaultValues?.unit : ''}
            name="unit"
            type="text"
            error={!!formHook?.formState.errors.unit}
            helperText={formHook?.formState.errors?.unit?.message?.toString()}
            fullWidth
            inputProps={{
              readOnly: unitReadOnlyStatus(isDisableInputs),
              startAdornment: '-',
            }}
            regExpToBlock={alphanumericRegExp}
          />
        }
      />
      <ToggleDisplayEditFormItem
        id="inputText-residentialAddress-postalCode"
        isEditState={isEditState}
        isHideDisplay
        containerClassName={classes.groupFormElementContainer}
        EditComponent={
          <>
            <TextFieldForm
              id="inputText-residentialAddress-postalCode"
              form={formHook}
              isInputTextUpperCase
              onChange={(e) => postcodeOnChange(e.target.value)}
              placeholder={t('profile->>CreateCandidateForm.Postal Code')}
              defaultValue={postalCodeDefaultValue(isNonNational, formDefaultValues)}
              name="postalCode"
              type="text"
              error={!!formHook?.formState.errors.postalCode}
              helperText={formHook?.formState.errors?.postalCode?.message?.toString()}
              className={classes.getAddressButton}
              inputProps={{
                readOnly: postalCodeReadOnlyStatus(isDisableInputs),
                startAdornment: 'Singapore',
              }}
              regExpToBlock={numericRegExp}
            />
            <Button
              id="button-validate-postcode"
              size="small"
              variant="outlined"
              onClick={validatePostalCode}
              disabled={isPostalCodeEmpty}
            >
              {t('profile->>CandidateProfileDetail.GetAddress')}
            </Button>
            {isApiError && (
              <Typography color="error">
                {t('profile->>CandidateProfileDetail.PostcodeApiErrorMsg')}{' '}
              </Typography>
            )}
          </>
        }
      />
      <HideOrShow
        isHide={!isEditState}
        Component={
          <FormControlLabel
            value="L"
            name="preferredAddress"
            control={<Radio />}
            label={formControlLabel ?? 'Preferred Address'}
            disabled={isDisableInputs?.preferredAddress}
          />
        }
      />
    </Box>
  );
};

export default ResidentialAddress;
